import {
  apiDeleteResult,
  apiGet,
  apiPost,
  apiPostResult,
  apiPutResult,
} from '@/services/api/http'
import { tokenStorage } from '@/services/auth/token-storage'
import type { AuthSession, AuthUser, LoginCredentials } from '@/types/auth.types'
import type { SystemRole } from '@/types/permission.types'
import { getDeviceId } from '@/lib/trusted-device'

/** Flat login payload from POST /auth/login and /auth/pin-login */
export type LoginResponseData = AuthUser & {
  accessToken: string
  refreshToken: string
  expiresAt: string
  authenticatedAt: string
  /** Device secret for PIN sign-in (null when the device is not trusted). */
  deviceToken?: string | null
}

/** A device trusted for PIN sign-in (GET /auth/trusted-devices). */
export interface TrustedDeviceRow {
  id: string
  device: { browser: string; os: string; type: 'desktop' | 'mobile' | 'tablet' }
  ip: string | null
  createdAt: string
  lastUsedAt: string
  current: boolean
}

/** One signed-in device (a session family). */
export interface DeviceSession {
  id: string
  device: { browser: string; os: string; type: 'desktop' | 'mobile' | 'tablet' }
  ip: string | null
  startedAt: string
  lastUsedAt: string
  expiresAt: string
  current: boolean
  /** Session belongs to a device trusted for PIN sign-in. */
  trusted?: boolean
  method?: 'password' | 'pin'
}

/** Security log row (GET /users/:id/auth-events). */
export interface AuthEvent {
  id: string
  type: string
  ip: string | null
  device: DeviceSession['device'] | null
  meta: Record<string, unknown> | null
  actor: { id: string; name: string } | null
  createdAt: string
}

export interface SecuritySettings {
  pinRequiredRoles: SystemRole[]
  idleMinutesDesktop: number
  idleMinutesMobile: number
  pinLoginRoles: SystemRole[]
  trustIdleDays: number
  trustPasswordDays: number
  idleOptions: number[]
  trustIdleDayOptions: number[]
  trustPasswordDayOptions: number[]
  updatedAt: string | null
}

export type RefreshResponseData = {
  accessToken: string
  refreshToken: string
  expiresAt: string
}

function toAuthUser(data: AuthUser): AuthUser {
  return {
    id: data.id,
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: data.phone,
    role: data.role,
    permissions: data.permissions ?? {},
    avatarUrl: data.avatarUrl ?? undefined,
    department: data.department ?? undefined,
    status: data.status,
    mustChangePassword: Boolean(data.mustChangePassword),
    security: data.security ?? undefined,
  }
}

function toSession(data: LoginResponseData): AuthSession {
  return {
    user: toAuthUser(data),
    authenticatedAt: data.authenticatedAt,
    expiresAt: data.expiresAt,
  }
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<{
    session: AuthSession
    deviceToken: string | null
    message?: string
  }> {
    const result = await apiPostResult<LoginResponseData>('/auth/login', {
      email: credentials.email,
      password: credentials.password,
      remember: Boolean(credentials.remember),
      trustDevice: Boolean(credentials.trustDevice),
      deviceId: getDeviceId(),
    })

    const payload = result.data
    if (!payload?.accessToken) {
      throw new Error(result.message || 'Login failed')
    }

    tokenStorage.setTokens(
      {
        accessToken: payload.accessToken,
        refreshToken: payload.refreshToken,
        expiresAt: payload.expiresAt,
      },
      Boolean(credentials.remember),
    )

    return {
      session: toSession(payload),
      deviceToken: payload.deviceToken ?? null,
      message: result.message,
    }
  },

  /** PIN sign-in on a trusted device; returns the rotated device secret. */
  async pinLogin(input: { userId: string; token: string; pin: string; remember: boolean }): Promise<{
    session: AuthSession
    deviceToken: string
  }> {
    const { data: payload } = await apiPostResult<LoginResponseData>('/auth/pin-login', {
      ...input,
      deviceId: getDeviceId(),
    })
    tokenStorage.setTokens(
      { accessToken: payload.accessToken, refreshToken: payload.refreshToken, expiresAt: payload.expiresAt },
      input.remember,
    )
    return { session: toSession(payload), deviceToken: payload.deviceToken ?? '' }
  },

  /** "Forgot password?" — notifies admins; the reply never says if the email exists. */
  /** 'email' = a 6-digit code was emailed (if the account exists); 'admin' = admins were notified. */
  async forgotPassword(email: string): Promise<'email' | 'admin'> {
    const { data } = await apiPostResult<{ method?: 'email' | 'admin' }>('/auth/forgot-password', { email })
    return data?.method === 'email' ? 'email' : 'admin'
  },

  /** Emailed code → one-time reset token for the new-password step. */
  async verifyResetCode(email: string, code: string): Promise<string> {
    const { data } = await apiPostResult<{ resetToken: string }>('/auth/reset-password/verify', { email, code })
    return data.resetToken
  },

  async resetPassword(resetToken: string, newPassword: string): Promise<void> {
    await apiPostResult('/auth/reset-password', { resetToken, newPassword })
  },

  /** Keep the password an admin set (new account / admin reset). */
  async keepPassword(): Promise<AuthUser> {
    const { data } = await apiPostResult<AuthUser>('/auth/keep-password')
    return toAuthUser(data)
  },

  /** Login screen "Forget this account" (no session needed). */
  async forgetDevice(userId: string, token: string): Promise<void> {
    await apiPostResult('/auth/device/forget', { userId, token, deviceId: getDeviceId() })
  },

  /** Confirm it's you before a sensitive action — PIN or password. */
  async stepUp(input: { pin: string } | { password: string }): Promise<void> {
    await apiPostResult('/auth/step-up', input)
  },

  async trustedDevices(): Promise<TrustedDeviceRow[]> {
    return apiGet<TrustedDeviceRow[]>('/auth/trusted-devices')
  },

  async removeTrustedDevice(id: string): Promise<void> {
    await apiDeleteResult(`/auth/trusted-devices/${id}`)
  },

  async logout(refreshToken?: string, forgetDevice = false): Promise<void> {
    await apiPost<void>('/auth/logout', {
      refreshToken: refreshToken ?? tokenStorage.getRefreshToken() ?? undefined,
      forgetDevice,
    })
  },

  /** Self-service change; the server keeps this session and ends the others. */
  async changePassword(currentPassword: string, newPassword: string): Promise<AuthUser> {
    const { data } = await apiPostResult<AuthUser>('/auth/change-password', {
      currentPassword,
      newPassword,
    })
    return toAuthUser(data)
  },

  async setPin(pin: string): Promise<void> {
    await apiPostResult('/auth/pin', { pin })
  },

  async changePin(input: { currentPin?: string; currentPassword?: string; newPin: string }): Promise<void> {
    await apiPutResult('/auth/pin', input)
  },

  async removePin(currentPassword: string): Promise<void> {
    await apiDeleteResult('/auth/pin', { data: { currentPassword } })
  },

  async lock(reason: 'idle' | 'manual'): Promise<void> {
    await apiPostResult('/auth/lock', { reason })
  },

  async unlock(input: { pin: string } | { password: string }): Promise<void> {
    await apiPostResult('/auth/unlock', input)
  },

  async getSecuritySettings(): Promise<SecuritySettings> {
    return apiGet<SecuritySettings>('/auth/settings')
  },

  async updateSecuritySettings(
    input: Partial<
      Omit<SecuritySettings, 'idleOptions' | 'trustIdleDayOptions' | 'trustPasswordDayOptions' | 'updatedAt'>
    >,
  ): Promise<SecuritySettings> {
    const { data } = await apiPutResult<SecuritySettings>('/auth/settings', input)
    return data
  },

  async sessions(): Promise<DeviceSession[]> {
    return apiGet<DeviceSession[]>('/auth/sessions')
  },

  async revokeSession(familyId: string): Promise<number> {
    const { data } = await apiDeleteResult<{ count: number }>(`/auth/sessions/${familyId}`)
    return data?.count ?? 0
  },

  async revokeOtherSessions(): Promise<number> {
    const { data } = await apiPostResult<{ count: number }>('/auth/sessions/revoke-others')
    return data?.count ?? 0
  },

  async me(): Promise<AuthUser> {
    const user = await apiGet<AuthUser>('/auth/me')
    return toAuthUser(user)
  },

  async refresh(): Promise<RefreshResponseData | null> {
    const tokens = await tokenStorage.refreshAccessToken()
    if (!tokens?.accessToken || !tokens.refreshToken || !tokens.expiresAt) {
      return null
    }
    return {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: tokens.expiresAt,
    }
  },
}
